import { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import { Component as CartIcon } from 'icons/icon-ui--cart.svg';
import { Component as ChevronDownIcon } from 'icons/icon-ui--chevron-down.svg';
import { Component as ChevronUpIcon } from 'icons/icon-ui--chevron-up.svg';
import { useQueryClient } from '@tanstack/react-query';
import { SignupProgressStepper } from './SignupProgressStepper';
import { SignupOrderSummary } from './SignupOrderSummary';
import { useMenuWithCart } from '../../contexts/MenuWithCart';
import { CartMinimumBanner } from '../shared/banner/CartMinimumBanner';
import styles from './SignupDrawer.module.scss';
import { getFormattedDeliveryDateRange } from '../../utils/dateTimes';
import { CouponBanner } from '../price-summary/CouponBanner';
import { SignupStep } from '../../constants/signup';
import { formatDeliveryDate } from '../../hooks/useFormattedDate';
import { useLoggedInMenuIdQuery } from '../../services/menu/menu.queries';

type Props = {
  children: ReactNode;
}
let lastSavedDate = '';

export function SignupDrawerLayout({ children }: Props): ReactElement {
  const queryClient = useQueryClient();
  const [deliveryDateText, setDeliveryDateText] = useState(() => lastSavedDate || 'Updating delivery date...');
  const [isOrderSummaryOpen, setIsOrderSummaryOpen] = useState(false);
  const {
    cart,
    menu,
    currentSignupStep,
  } = useMenuWithCart();
  const remainingMinimumAmount = cart?.priceSummary?.remainingMinimumAmount;
  const deliveryDate = menu?.deliveryOn || '';
  const menuId = menu?.id;
  const isPaying = currentSignupStep === SignupStep.PAYMENT;
  const formattedEstimatedDeliveryDate = getFormattedDeliveryDateRange(deliveryDate, false);
  const {
    data: loggedinMenu,
    isLoading,
  } = useLoggedInMenuIdQuery(menuId, isPaying);
  const deliveryOn = loggedinMenu?.deliveryOn;
  const deliveryDateFormatted = useMemo(() => formatDeliveryDate(deliveryOn), [deliveryOn]);
  const isFetching = queryClient.isFetching({ queryKey: ['loggedInMenu', menuId] });
  const couponAmount = cart?.priceSummary?.couponAmount;
  const couponCode = cart?.priceSummary?.couponName;
  const isCouponApplied = cart?.priceSummary?.isCouponApplied;
  const showCouponText = couponAmount != null && couponCode != null && isCouponApplied;

  useEffect(() => {
    if (deliveryDateFormatted) {
      setDeliveryDateText(deliveryDateFormatted);
      lastSavedDate = deliveryDateFormatted;
    } else if (isLoading || isFetching) {
      setDeliveryDateText('Updating delivery date...');
    }
  }, [loggedinMenu?.deliveryOn, isLoading, isFetching]);

  return (
    <div className={styles.signup_drawer_container}>
      <button
        type="button"
        onClick={() => setIsOrderSummaryOpen(!isOrderSummaryOpen)}
        className={styles.order_summary_button}
        data-heap-id={`${isOrderSummaryOpen ? 'close' : 'open'}_signup_order_summary`}
      >
        <div className={styles.order_summary_button_content}>
          <CartIcon height="10px" />
          Order Summary
          {deliveryDate !== '' && (
            <>
              {' '}
              |
              {' '}
              {isPaying ? deliveryDateText : formattedEstimatedDeliveryDate}
            </>
          )}
        </div>
        <div className={styles.order_summary_button_content}>
          <span style={{ fontWeight: 'bold' }}>{cart?.priceSummary?.total}</span>
          {isOrderSummaryOpen ? <ChevronUpIcon width="10px" /> : <ChevronDownIcon width="10px" />}
        </div>
      </button>
      {!!remainingMinimumAmount && (
        <div style={{ position: 'unset' }}>
          <CartMinimumBanner remainingCartMinimum={remainingMinimumAmount} cartMinimum={cart?.priceSummary?.minimumBasePrice ?? ''} />
        </div>
      )}

      {isOrderSummaryOpen ? (
        <>
          {showCouponText && (
            <div className={styles.coupon}>
              <CouponBanner couponAmount={couponAmount} couponCode={couponCode} />
            </div>
          )}
          <SignupOrderSummary />
        </>
      ) : (
        <>
          <SignupProgressStepper />
          <div className={styles.signup_step_content_container}>
            {children}
          </div>
        </>
      )}
    </div>
  );
}
